import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../CSS/CurrentSearch.css';

interface JobListing {
  id: number;
  icon: string;
  jobTitle: string;
  school: string;
  postingTime: string;
  company: string;
  location: string;
  description: string;
  applicationLink?: string;
  profileLink?: string;
  nominateLink?: string;
}

interface JobFromAPI {
  jobId: number;
  uniAffil: string;
  uniLogo: string;
  jobTitle: string;
  jobLocation: string;
  applicationLink: string;
  jobProfile: string;
  nominateLink: string;
  jobDesc: string;
  createdDate: string;
}

const CurrentSearch: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isFromAdmin = searchParams.get('fromAdmin') === 'true';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [jobListings, setJobListings] = useState<JobListing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Placeholder image path
  const PLACEHOLDER_IMAGE = '/assets/Web Version/image-thumbnail-image.png';

  // Format date to readable string
  const formatPostingTime = (dateString: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) {
      return 'Posted less than an hour ago';
    } else if (diffInHours < 24) {
      return `Posted today, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (diffInDays === 1) {
      return `Posted yesterday, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      return `Posted on ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}, at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
  };

  // Helper function to strip HTML tags from text for searching
  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Fetch jobs from API
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('http://127.0.0.1:3060/api/admin/create-job/available');
        const data = await response.json();

        if (data.success && data.data?.jobs) {
          const jobs: JobListing[] = data.data.jobs.map((job: JobFromAPI) => {
            // Check if uniLogo is empty, null, or undefined
            const hasValidLogo = job.uniLogo && job.uniLogo.trim() !== '';
            
            // Debug logging
            if (!hasValidLogo) {
              console.warn('Job missing uniLogo:', { jobId: job.jobId, jobTitle: job.jobTitle, job });
            }
            
            return {
              id: job.jobId,
              icon: hasValidLogo ? `http://127.0.0.1:3060/${job.uniLogo}` : PLACEHOLDER_IMAGE,
              jobTitle: job.jobTitle,
              school: job.uniAffil,
              postingTime: formatPostingTime(job.createdDate),
              company: job.uniAffil,
              location: job.jobLocation,
              description: job.jobDesc || '',
              applicationLink: job.applicationLink || '',
              profileLink: job.jobProfile ? `http://127.0.0.1:3060/${job.jobProfile}` : '',
              nominateLink: job.nominateLink || ''
            };
          });

          setJobListings(jobs);
          
          // Set first job as selected by default
          if (jobs.length > 0) {
            setSelectedJob(jobs[0]);
          }
        } else {
          setJobListings([]);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setJobListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

  // Filter jobs based on search query - search through all available job fields
  const filteredJobs = searchQuery.trim() === '' 
    ? jobListings 
    : jobListings.filter(job => {
        const searchLower = searchQuery.toLowerCase();
        const descriptionText = stripHtmlTags(job.description).toLowerCase();
        
        return (
          job.jobTitle.toLowerCase().includes(searchLower) ||
          job.school.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower) ||
          descriptionText.includes(searchLower)
        );
      });

  // Update selected job when filtered results change
  useEffect(() => {
    if (filteredJobs.length > 0) {
      // If current selected job is not in filtered results, select first filtered job
      const isSelectedJobInResults = selectedJob && filteredJobs.find(job => job.id === selectedJob.id);
      if (!isSelectedJobInResults) {
        setSelectedJob(filteredJobs[0]);
      }
    } else if (searchQuery.trim() !== '') {
      // If search query exists but no results, clear selection
      setSelectedJob(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredJobs, searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Filter jobs based on search query - search through all fields
    const filtered = jobListings.filter(job => {
      const searchLower = searchQuery.toLowerCase();
      const descriptionText = stripHtmlTags(job.description).toLowerCase();
      
      return (
        job.jobTitle.toLowerCase().includes(searchLower) ||
        job.school.toLowerCase().includes(searchLower) ||
        job.location.toLowerCase().includes(searchLower) ||
        descriptionText.includes(searchLower)
      );
    });
    
    // If search returns results, select first one; otherwise clear selection
    if (filtered.length > 0) {
      setSelectedJob(filtered[0]);
    } else {
      setSelectedJob(null);
    }
  };

  const handleJobSelect = (job: JobListing) => {
    setSelectedJob(job);
  };

  const handleJobToggle = (job: JobListing) => {
    if (expandedJobId === job.id) {
      setExpandedJobId(null);
    } else {
      setExpandedJobId(job.id);
      setSelectedJob(job);
    }
  };

  return (
    <div className="current-search-page">
      <div 
        className="current-search-container"
        style={isFromAdmin ? { marginTop: '10vh' } : {}}
      >
        <form className="current-search-form" onSubmit={handleSearchSubmit}>
          <div className="current-search-bar">
            <input
              type="text"
              className="current-search-input"
              placeholder="Search for a keyword or university name"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button type="submit" className="current-search-icon-button" aria-label="Search">
              <svg
                className="current-search-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>

        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading jobs...</div>
        ) : searchQuery.trim() !== '' && filteredJobs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            No jobs found matching your search. Try different keywords.
          </div>
        ) : filteredJobs.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            There are no jobs at this time. Please check back again at a later date.
          </div>
        ) : (
          <div className="current-search-content">
            {/* Desktop: Two-column layout */}
            {/* Left Column - Job Listings */}
            <div className="current-search-listings">
              {filteredJobs.map((job) => (
                <button
                  key={job.id}
                  className={`current-search-listing-item ${selectedJob?.id === job.id ? 'active' : ''}`}
                  onClick={() => handleJobSelect(job)}
                >
                  <div className="current-search-listing-icon">
                    <img src={job.icon} alt={job.school} onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                    }} />
                  </div>
                  <div className="current-search-listing-content">
                    <div className="current-search-listing-title">{job.jobTitle}</div>
                    <div className="current-search-listing-school">{job.school}</div>
                    <div className="current-search-listing-time">{job.postingTime}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right Column - Job Details */}
            <div className="current-search-details">
              {!selectedJob ? (
                <div style={{ padding: '2rem', textAlign: 'center' }}>Select a job to view details</div>
              ) : (
                <>
                  <div className="current-search-details-header">
                    <div className="current-search-details-header-left">
                      <div className="current-search-details-icon">
                        <img src={selectedJob.icon} alt={selectedJob.company} onError={(e) => {
                          (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                        }} />
                      </div>
                      <div className="current-search-details-company-info">
                        <div className="current-search-details-company-name">{selectedJob.company}</div>
                      </div>
                    </div>
                    <div className="current-search-details-timestamp">{selectedJob.postingTime}</div>
                  </div>

                  <div className="current-search-details-title">{selectedJob.jobTitle}</div>
                  <div className="current-search-details-company">{selectedJob.location}</div>

                  <div className="current-search-details-buttons">
                    {selectedJob.applicationLink && (
                      <a 
                        href={selectedJob.applicationLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="current-search-button current-search-button-primary"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                      >
                        Submit Application
                      </a>
                    )}
                    {selectedJob.profileLink && (
                      <a 
                        href={selectedJob.profileLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="current-search-button current-search-button-secondary"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                      >
                        View Profile
                      </a>
                    )}
                    {selectedJob.nominateLink && (
                      <a 
                        href={selectedJob.nominateLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="current-search-button current-search-button-secondary"
                        style={{ textDecoration: 'none', display: 'inline-block' }}
                      >
                        Nominate
                      </a>
                    )}
                  </div>

                  <div 
                    className="current-search-details-description"
                    dangerouslySetInnerHTML={{ __html: selectedJob.description }}
                  />
                </>
              )}
            </div>

            {/* Mobile: Collapsible sections */}
            <div className="current-search-collapsible-list">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className={`current-search-collapsible-item ${expandedJobId === job.id ? 'expanded' : ''}`}
                >
                  <button
                    className="current-search-collapsible-header"
                    onClick={() => handleJobToggle(job)}
                  >
                    <div className="current-search-collapsible-header-content">
                      <div className="current-search-collapsible-icon">
                        <img src={job.icon} alt={job.school} onError={(e) => {
                          (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
                        }} />
                      </div>
                      <div className="current-search-collapsible-info">
                        <div className="current-search-collapsible-title">{job.jobTitle}</div>
                        <div className="current-search-collapsible-school">{job.school}</div>
                        <div className="current-search-collapsible-time">{job.postingTime}</div>
                      </div>
                    </div>
                    <div className="current-search-collapsible-arrow">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                  <div className="current-search-collapsible-content">
                    <div className="current-search-details-title">{job.jobTitle}</div>
                    <div className="current-search-details-company">{job.location}</div>

                    <div className="current-search-details-buttons">
                      {job.applicationLink && (
                        <a 
                          href={job.applicationLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="current-search-button current-search-button-primary"
                          style={{ textDecoration: 'none', display: 'inline-block' }}
                        >
                          Submit Application
                        </a>
                      )}
                      {job.profileLink && (
                        <a 
                          href={job.profileLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="current-search-button current-search-button-secondary"
                          style={{ textDecoration: 'none', display: 'inline-block' }}
                        >
                          View Profile
                        </a>
                      )}
                      {job.nominateLink && (
                        <a 
                          href={job.nominateLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="current-search-button current-search-button-secondary"
                          style={{ textDecoration: 'none', display: 'inline-block' }}
                        >
                          Nominate
                        </a>
                      )}
                    </div>

                    <div 
                      className="current-search-details-description"
                      dangerouslySetInnerHTML={{ __html: job.description }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentSearch;

